import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { ApiFetch, ApiStream } from '../utils/api';
import {
  ActiveNarratorIndexSet,
  BubbleClass,
  BubbleRowClass,
  CHALLENGE_1,
  CreateReplyPicker,
  CreateNegativeReplyPicker,
  CreateSequentialPicker,
  DisplayMessageBody,
  DIG_CHAT_NARRATOR,
  FormatDigChatNarrator,
  GOON_STALLS,
  HOLD_MUSIC_LINES,
  HOLD_MUSIC_RIDDLER_TAUNTS,
  HoldMusicMedley,
  HoldMusicTeaserLine,
  IsCorrectChallenge1,
  IsHoldMusicMedley,
  MakeLocalGoonMessage,
  MakeLocalNarratorMessage,
  MakeLocalRiddlerMessage,
  MessageBodyTokens,
  OFFER_HOLD_LINES,
  SenderLabel,
  StripExposedGitopsUrl,
} from '../utils/challenges';
import { EnablePushNotifications, NotifyIfTabHidden, PushPermissionState, PushSupported, SyncPushSubscription } from '../utils/push';

function FormatTime(iso) {
  if (!iso) return '';
  try {
    const when = new Date(iso);
    if (Number.isNaN(when.getTime())) return '';
    return when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

function FormatCountdown(expiresAtIso, nowMs) {
  if (!expiresAtIso) return null;
  const end = new Date(expiresAtIso).getTime();
  if (Number.isNaN(end)) return null;
  const remainingMs = Math.max(0, end - nowMs);
  const totalSec = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function PrefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Split Narrator copy on blank lines. Authors own the reveal beats. */
function ParagraphTokens(text) {
  return (text || '')
    .split(/\n\n+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function StageForAge(age) {
  if (age <= 0) return 'is-lead';
  if (age === 1) return 'is-trail';
  return 'is-settled';
}

function WaitingRoomPanel({ line, ticket, board, open, onToggle, nowMs }) {
  const lineLabel = line === 'snooze' ? 'Snooze Lounge' : 'Main Conga';
  const stub = ticket ? `${lineLabel} #${ticket}` : lineLabel;
  const panelId = 'funhouse-waiting-room';
  const claims = board?.claims || [];

  return (
    <div className={`waiting-room${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="waiting-room-toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="waiting-room-stub">{stub}</span>
        <span className="waiting-room-cue">
          {open ? 'Hide waiting room' : 'Show waiting room'}
        </span>
      </button>
      <div
        id={panelId}
        className="waiting-room-panel"
        aria-hidden={!open}
      >
        <div className="waiting-room-panel-inner">
          <p className="waiting-room-serving" aria-live="polite">
            {board?.serving || 'NOW SERVING:'}
          </p>
          {board?.you_are_next && !claims.length && (
            <p className="waiting-room-window">You're next in line</p>
          )}
          {board?.reason && (
            <p className="waiting-room-reason">{board.reason}</p>
          )}
          <ul className="waiting-room-lines">
            <li className="waiting-room-line">
              <span className="waiting-room-line-label">Claim window</span>
              <span className="waiting-room-line-tickets">
                {claims.length === 0 ? (
                  <span className="is-empty">Empty</span>
                ) : (
                  claims.map((entry, index) => {
                    const left = FormatCountdown(entry.offer_expires_at, nowMs);
                    return (
                      <React.Fragment key={`claim-${entry.ticket}`}>
                        {index > 0 && <span className="waiting-room-sep"> · </span>}
                        <span className={entry.you ? 'is-you' : undefined}>
                          #{entry.ticket}
                          {entry.you ? ' you' : ''}
                          {left ? ` (${left})` : ''}
                        </span>
                      </React.Fragment>
                    );
                  })
                )}
              </span>
            </li>
            {(board?.lines || []).map((row) => (
              <li key={row.id} className="waiting-room-line">
                <span className="waiting-room-line-label">{row.label}</span>
                <span className="waiting-room-line-tickets">
                  {(row.tickets || []).length === 0 ? (
                    <span className="is-empty">Empty</span>
                  ) : (
                    row.tickets.map((entry, index) => (
                      <React.Fragment key={`${row.id}-${entry.ticket}`}>
                        {index > 0 && <span className="waiting-room-sep"> · </span>}
                        <span className={entry.you ? 'is-you' : undefined}>
                          #{entry.ticket}
                          {entry.you ? ' you' : ''}
                        </span>
                      </React.Fragment>
                    ))
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function RenderStaticTokens(tokens) {
  return tokens.map((token, index) => {
    if (token.type === 'room') {
      return (
        <strong key={`room-${index}`} className="room-name">{token.name}</strong>
      );
    }
    const value = token.value;
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = linkRegex.exec(value)) !== null) {
      if (match.index > lastIndex) {
        parts.push(value.slice(lastIndex, match.index));
      }
      parts.push(<a key={`link-${match.index}`} href={match[2]} target="_blank" rel="noopener noreferrer" className="chat-link">{match[1]}</a>);
      lastIndex = linkRegex.lastIndex;
    }
    if (lastIndex < value.length) {
      parts.push(value.slice(lastIndex));
    }
    return <span key={`text-${index}`}>{parts.length > 1 ? parts : value}</span>;
  });
}

function MessageText({ sender, body, roomName, speak, onRevealDone, onRevealProgress }) {
  const fullText = StripExposedGitopsUrl(DisplayMessageBody(sender, body), null);
  // Narrator reveals by authored paragraph (`\\n\\n`). Hold-music lyrics already use that.
  const units = useMemo(() => ParagraphTokens(fullText), [fullText]);
  const shouldReveal = Boolean(speak && sender === 'narrator' && !PrefersReducedMotion());
  const [visibleCount, setVisibleCount] = useState(() => (shouldReveal ? 0 : Math.max(units.length, 1)));
  const [settleExtra, setSettleExtra] = useState(0);
  const doneRef = useRef(false);
  const onDoneRef = useRef(onRevealDone);
  const onProgressRef = useRef(onRevealProgress);
  onDoneRef.current = onRevealDone;
  onProgressRef.current = onRevealProgress;

  useEffect(() => {
    doneRef.current = false;
    setSettleExtra(0);
    const Finish = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      onDoneRef.current?.();
    };
    const Progress = () => {
      onProgressRef.current?.();
    };

    const unitCount = Math.max(units.length, 1);

    if (!shouldReveal) {
      setVisibleCount(unitCount);
      if (speak && sender === 'narrator') Finish();
      return undefined;
    }

    setVisibleCount(0);
    let index = 0;
    let timerId;

    const RunCoda = () => {
      timerId = window.setTimeout(() => {
        setSettleExtra(1);
        Progress();
        timerId = window.setTimeout(() => {
          setSettleExtra(2);
          Progress();
          timerId = window.setTimeout(Finish, 220);
        }, 480);
      }, 480);
    };

    const Tick = () => {
      index += 1;
      setVisibleCount(index);
      Progress();
      if (index >= unitCount) {
        RunCoda();
        return;
      }
      // Pause between paragraphs so each stage direction lands.
      timerId = window.setTimeout(Tick, 900);
    };
    timerId = window.setTimeout(Tick, 220);
    return () => window.clearTimeout(timerId);
  }, [shouldReveal, speak, sender, fullText, units.length]);

  if (shouldReveal) {
    const paras = units.length ? units : [fullText];
    const visibleUnits = paras.slice(0, visibleCount);
    return (
      <p className="bubble-text is-revealing is-multi">
        {visibleUnits.map((para, index) => {
          const age = visibleCount + settleExtra - (index + 1);
          const stage = StageForAge(age);
          return (
            <span key={`para-${index}`}>
              {index > 0 ? '\n\n' : ''}
              <span className={`narrator-word ${stage}`}>
                {RenderStaticTokens(MessageBodyTokens(para, roomName))}
              </span>
            </span>
          );
        })}
      </p>
    );
  }

  return (
    <p className="bubble-text">
      {RenderStaticTokens(MessageBodyTokens(fullText, roomName))}
    </p>
  );
}

export default function Chat() {
  const { user, logout, resetAccount, nukeAllUsers, testMode } = useAuth();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendPhase, setSendPhase] = useState(null); // null | 'sending' | 'waiting'
  const [claiming, setClaiming] = useState(false);
  const [exploring, setExploring] = useState(false);
  const [exploreAction, setExploreAction] = useState(null);
  const exploringRef = useRef(false);
  const [waking, setWaking] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [pushStatus, setPushStatus] = useState('');
  const [pushState, setPushState] = useState('default');
  const lastHiddenNotifyRef = useRef('');
  const [copied, setCopied] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [historyReady, setHistoryReady] = useState(false);
  const [instantNarratorIds, setInstantNarratorIds] = useState(() => new Set());
  const [unlockedCount, setUnlockedCount] = useState(0);
  const [awaitingNarratorReveal, setAwaitingNarratorReveal] = useState(false);
  const [showPlaySong, setShowPlaySong] = useState(false);
  const [holdSongNextIndex, setHoldSongNextIndex] = useState(0);
  const [waitingRoomOpen, setWaitingRoomOpen] = useState(false);
  const [workEmail, setWorkEmail] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [editingWorkEmail, setEditingWorkEmail] = useState(false);
  const historyIdsRef = useRef(new Set());
  const waitingOnRevealRef = useRef(false);
  const pendingProgressRef = useRef(null);
  const holdSongIntroRef = useRef(false);
  const screenRef = useRef(null);
  const threadRef = useRef(null);
  const dockRef = useRef(null);
  const composerInputRef = useRef(null);
  const bottomRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const pickNegativeReplyRef = useRef(CreateNegativeReplyPicker());
  const pickDigChatNarratorRef = useRef(CreateReplyPicker(DIG_CHAT_NARRATOR));
  const pickGoonStallRef = useRef(CreateReplyPicker(GOON_STALLS));
  const pickOfferHoldRef = useRef(CreateSequentialPicker(OFFER_HOLD_LINES));
  const pickHoldSongTauntRef = useRef(CreateReplyPicker(HOLD_MUSIC_RIDDLER_TAUNTS));
  const lastViewportHeightRef = useRef(
    typeof window !== 'undefined' ? window.visualViewport?.height ?? window.innerHeight : 0,
  );

  const IsHoldMusicLine = (msg) => (
    msg?.sender === 'narrator' && (msg.body || '').includes('🎵')
  );

  const ResetHoldMusic = () => {
    holdSongIntroRef.current = false;
    setShowPlaySong(false);
    setHoldSongNextIndex(0);
  };

  /** Sync + state: hide CTAs before the next paint can flash the next button set. */
  const ArmNarratorRevealGate = () => {
    waitingOnRevealRef.current = true;
    setAwaitingNarratorReveal(true);
  };

  /**
   * While a Narrator beat is revealing, keep most CTA flags on the previous
   * progress snapshot so Enter / Open laptop cannot pop in for one frame.
   * Explore completion flags still apply so Look around cannot be mashed again.
   */
  const QueueProgress = (next) => {
    if (!next) return;
    if (waitingOnRevealRef.current) {
      pendingProgressRef.current = next;
      setProgress((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          hold_expires_at: next.hold_expires_at ?? prev.hold_expires_at,
          offer_expires_at: next.offer_expires_at ?? prev.offer_expires_at,
          now_seeing: next.now_seeing ?? prev.now_seeing,
          waiting_room: next.waiting_room ?? prev.waiting_room,
          // Explore flags must apply immediately so Look around / Peel / etc.
          // cannot be mashed again while the Narrator is still revealing.
          explore_looked: next.explore_looked ?? prev.explore_looked,
          explore_mirror: next.explore_mirror ?? prev.explore_mirror,
          explore_poster: next.explore_poster ?? prev.explore_poster,
          explore_closet_ready: next.explore_closet_ready ?? prev.explore_closet_ready,
          explore_laptop_ready: next.explore_laptop_ready ?? prev.explore_laptop_ready,
        };
      });
      return;
    }
    pendingProgressRef.current = null;
    setProgress(next);
  };

  const FlushPendingProgress = () => {
    const pending = pendingProgressRef.current;
    if (!pending) return;
    pendingProgressRef.current = null;
    setProgress(pending);
  };

  const NearThreadBottom = (thresholdPx = 140) => {
    const thread = threadRef.current;
    if (!thread) return true;
    return thread.scrollHeight - thread.scrollTop - thread.clientHeight <= thresholdPx;
  };

  const ScrollThreadToBottom = (behavior = 'auto', { force = false } = {}) => {
    const thread = threadRef.current;
    if (!thread) return;
    if (!force && !stickToBottomRef.current && !NearThreadBottom()) return;
    stickToBottomRef.current = true;
    const Stick = () => {
      thread.scrollTop = thread.scrollHeight;
      bottomRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    };
    Stick();
    requestAnimationFrame(() => {
      Stick();
      requestAnimationFrame(Stick);
    });
  };

  const ScheduleScrollToBottom = (force = true) => {
    ScrollThreadToBottom('auto', { force });
    window.setTimeout(() => ScrollThreadToBottom('auto', { force }), 50);
    // Explore/enter buttons animate in (~450ms); catch the moved goal posts.
    window.setTimeout(() => ScrollThreadToBottom('auto', { force }), 320);
    window.setTimeout(() => ScrollThreadToBottom('auto', { force }), 520);
  };

  const ApplyRoomPayload = (payload) => {
    const incoming = Array.isArray(payload?.messages) ? payload.messages : [];
    const hasNarrator = incoming.some((m) => m?.sender === 'narrator');
    if (hasNarrator) {
      ArmNarratorRevealGate();
    }
    if (incoming.length) {
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const next = [...prev];
        incoming.forEach((msg) => {
          if (!seen.has(msg.id)) next.push(msg);
        });
        return next;
      });
    }
    if (payload?.progress) QueueProgress(payload.progress);
  };

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const ClearKeyboardLayout = (screen) => {
      screen.classList.remove('chat-screen--keyboard');
      screen.style.height = '';
      screen.style.minHeight = '';
      screen.style.maxHeight = '';
      screen.style.transform = '';
    };

    const SyncViewport = () => {
      const screen = screenRef.current;
      if (!screen) return;

      const prevHeight = lastViewportHeightRef.current;
      // Keep CSS full-bleed at rest. Only pin to visualViewport while the
      // keyboard (or browser chrome) is shoving the page.
      const heightGap = window.innerHeight - vv.height;
      const focused = (() => {
        const el = document.activeElement;
        if (!el) return false;
        const tag = (el.tagName || '').toLowerCase();
        return tag === 'textarea' || tag === 'input' || el.isContentEditable;
      })();
      const keyboardOpen = heightGap > 60 || vv.offsetTop > 0 || (
        focused && heightGap > 24
      );

      if (keyboardOpen) {
        // Override min-height: 100dvh so the dock can sit above the keyboard.
        const heightPx = `${Math.round(vv.height)}px`;
        screen.classList.add('chat-screen--keyboard');
        screen.style.height = heightPx;
        screen.style.minHeight = heightPx;
        screen.style.maxHeight = heightPx;
        screen.style.transform = vv.offsetTop
          ? `translateY(${Math.round(vv.offsetTop)}px)`
          : '';
      } else {
        ClearKeyboardLayout(screen);
      }

      lastViewportHeightRef.current = vv.height;
      if (keyboardOpen && (vv.height < prevHeight - 40 || focused)) {
        ScrollThreadToBottom('auto', { force: true });
      }
    };

    const OnVisibility = () => {
      if (document.visibilityState === 'visible') SyncViewport();
    };

    // iOS keyboard animation lags focus; a couple of delayed syncs catch the final size.
    const OnFocusIn = (event) => {
      const tag = (event.target?.tagName || '').toLowerCase();
      if (tag !== 'textarea' && tag !== 'input') return;
      SyncViewport();
      window.setTimeout(SyncViewport, 50);
      window.setTimeout(SyncViewport, 300);
    };

    const OnFocusOut = () => {
      window.setTimeout(SyncViewport, 50);
      window.setTimeout(SyncViewport, 300);
    };

    SyncViewport();
    vv.addEventListener('resize', SyncViewport);
    vv.addEventListener('scroll', SyncViewport);
    window.addEventListener('orientationchange', SyncViewport);
    document.addEventListener('visibilitychange', OnVisibility);
    document.addEventListener('focusin', OnFocusIn);
    document.addEventListener('focusout', OnFocusOut);
    return () => {
      vv.removeEventListener('resize', SyncViewport);
      vv.removeEventListener('scroll', SyncViewport);
      window.removeEventListener('orientationchange', SyncViewport);
      document.removeEventListener('visibilitychange', OnVisibility);
      document.removeEventListener('focusin', OnFocusIn);
      document.removeEventListener('focusout', OnFocusOut);
      if (screenRef.current) ClearKeyboardLayout(screenRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      ApiFetch('/messages'),
      ApiFetch('/game/progress').catch(() => null),
    ])
      .then(([msgs, prog]) => {
        if (cancelled) return;
        console.log('[Chat] Initial load - msgs:', msgs, 'prog:', prog);
        const loaded = msgs || [];
        // Messages already in the thread for a while stay static; fresh ones (e.g. cold open) speak.
        const historyCutoff = Date.now() - 8000;
        historyIdsRef.current = new Set(
          loaded
            .filter((m) => {
              const t = new Date(m.created_at).getTime();
              return Number.isFinite(t) && t < historyCutoff;
            })
            .map((m) => m.id),
        );
        setMessages(loaded);
        setProgress(prog);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setHistoryReady(true);
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const needsTick = (
      progress?.room_state === 'offered'
      || progress?.room_state === 'held'
      || progress?.room_state === 'asleep'
      || progress?.room_state === 'waiting'
    );
    if (!needsTick) return undefined;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [progress?.room_state]);
  useEffect(() => {
    ScrollThreadToBottom(unlockedCount <= 1 ? 'auto' : 'smooth', { force: true });
  }, [unlockedCount, messages.length]);

  // Track whether the user is pinned to the latest message.
  useEffect(() => {
    const thread = threadRef.current;
    if (!thread) return undefined;
    const OnScroll = () => {
      stickToBottomRef.current = NearThreadBottom();
    };
    thread.addEventListener('scroll', OnScroll, { passive: true });
    return () => thread.removeEventListener('scroll', OnScroll);
  }, []);

  // Composer-dock growth (new CTAs) shrinks the thread without changing message
  // count — ResizeObserver keeps the last bubble above the new buttons.
  useEffect(() => {
    const dock = dockRef.current;
    const thread = threadRef.current;
    if (typeof ResizeObserver === 'undefined' || (!dock && !thread)) return undefined;
    const observer = new ResizeObserver(() => {
      if (stickToBottomRef.current) {
        ScheduleScrollToBottom(true);
      }
    });
    if (dock) observer.observe(dock);
    if (thread) observer.observe(thread);
    return () => observer.disconnect();
  }, []);

  // Lazy settle + message pull while Challenge 2/3/4 coin flow are live.
  useEffect(() => {
    const challenge = Number(progress?.current_challenge);
    const coinFlow = progress?.coin_status;
    const needPoll = (
      challenge === 2
      || challenge === 3
      || coinFlow === 'awaiting_email'
      || coinFlow === 'pending_booth'
    );
    if (!needPoll) return undefined;
    if (progress?.is_completed && coinFlow === 'claimed') return undefined;
    let cancelled = false;
    const MergeMessages = (msgs) => {
      if (!Array.isArray(msgs) || !msgs.length) return;
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const next = [...prev];
        msgs.forEach((msg) => {
          if (msg?.id && !seen.has(msg.id)) next.push(msg);
        });
        return next.length === prev.length ? prev : next;
      });
    };
    const Tick = async () => {
      try {
        // Fetch both before any setState so a C2→C3 progress update cannot
        // cancel this effect before escape messages are merged.
        const [payload, msgs] = await Promise.all([
          ApiFetch('/game/room-status').catch(() => null),
          ApiFetch('/messages').catch(() => null),
        ]);
        if (cancelled) return;
        MergeMessages(msgs);
        if (payload) ApplyRoomPayload(payload);
      } catch (err) {
        // 401 triggers global logout via ApiFetch; surface other errors.
        if (!cancelled && err?.message && err.message !== 'Not authenticated') {
          setError(err.message);
        }
      }
    };
    Tick();
    const holdLeftMs = progress?.hold_expires_at
      ? new Date(progress.hold_expires_at).getTime() - Date.now()
      : null;
    const urgent = (
      Boolean(progress?.goon_wire_severed)
      || (
        progress?.room_state === 'held'
        && Number.isFinite(holdLeftMs)
        && holdLeftMs <= 15_000
      )
    );
    const id = setInterval(
      Tick,
      urgent ? 2000 : (progress?.room_state === 'held' ? 5000 : 8000),
    );
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [progress?.current_challenge, progress?.room_state, progress?.hold_expires_at, progress?.goon_wire_severed, progress?.is_completed]);

  const handleCopyUsername = async () => {
    const name = user?.username;
    if (!name) return;
    try {
      await navigator.clipboard.writeText(name);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setError('Could not copy username');
    }
  };

  const handleEnablePush = async () => {
    setPushStatus('');
    try {
      await EnablePushNotifications();
      setPushState('subscribed');
      setPushStatus('Alerts on. I just poked you. If you felt it, we are good.');
    } catch (err) {
      setPushStatus(err.message);
      PushPermissionState().then(setPushState).catch(() => {});
    }
  };

  useEffect(() => {
    if (!PushSupported()) return undefined;
    let cancelled = false;
    (async () => {
      const state = await PushPermissionState().catch(() => 'unsupported');
      if (cancelled) return;
      setPushState(state);
      // Re-POST local subscription so Conga pushes have a live Apple endpoint.
      if (state === 'subscribed' || state === 'granted') {
        try {
          await SyncPushSubscription({ sendTest: false });
          if (!cancelled) setPushState('subscribed');
        } catch {
          /* keep prior UI state; Enable can recover */
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // If the player wandered to another tab, ping when the story jumps.
  useEffect(() => {
    if (!progress) return;
    const harbor = (progress.harbor_url || '').trim();
    const key = [
      progress.current_challenge,
      progress.room_state,
      progress.goon_wire_severed ? 'wire' : '',
      harbor,
    ].join('|');
    if (!key || key === lastHiddenNotifyRef.current) return;

    let title = '';
    let body = '';
    if (harbor && Number(progress.current_challenge) === 3) {
      title = 'Funhouse collapsing';
      body = 'Challenge 2 just came down. Chat has the aftermath.';
    } else if (progress.goon_wire_severed && Number(progress.current_challenge) === 2) {
      title = 'Uh oh--';
      body = 'Night wire flopped. Funhouse chat is yelling.';
    } else if (progress.room_state === 'offered') {
      title = 'NOW SERVING: you';
      body = 'A Funhouse door is open. Two minutes.';
    } else {
      return;
    }

    const sent = NotifyIfTabHidden({ title, body, tag: `riddler-${key}` });
    if (sent) lastHiddenNotifyRef.current = key;
  }, [
    progress?.current_challenge,
    progress?.room_state,
    progress?.goon_wire_severed,
    progress?.harbor_url,
  ]);

  const handleClearGame = async () => {
    const result = await ApiFetch('/game/clear', { method: 'POST' });
    pickNegativeReplyRef.current = CreateNegativeReplyPicker();
    pickDigChatNarratorRef.current = CreateReplyPicker(DIG_CHAT_NARRATOR);
    pickGoonStallRef.current = CreateReplyPicker(GOON_STALLS);
    pickOfferHoldRef.current = CreateSequentialPicker(OFFER_HOLD_LINES);
    pickHoldSongTauntRef.current = CreateReplyPicker(HOLD_MUSIC_RIDDLER_TAUNTS);
    ResetHoldMusic();
    pendingProgressRef.current = null;
    waitingOnRevealRef.current = false;
    setAwaitingNarratorReveal(false);
    setProgress(result.progress || null);
    setMessages(result.messages || []);
    setDraft('');
  };

  const handleGoonFound = async () => {
    const result = await ApiFetch('/game/goon-found', { method: 'POST' });
    ApplyRoomPayload(result);
    setDraft('');
  };

  const handleReport = async () => {
    const result = await ApiFetch('/admin/report', { method: 'POST' });
    if (result.message) {
      setInstantNarratorIds((prev) => {
        const next = new Set(prev);
        next.add(result.message.id);
        return next;
      });
      setMessages((prev) => [...prev, result.message]);
    }
    setDraft('');
  };

  const ResyncChatFromServer = async ({ deferCtas = false } = {}) => {
    const [payload, msgs] = await Promise.all([
      ApiFetch('/game/room-status').catch(() => null),
      ApiFetch('/messages').catch(() => null),
    ]);
    // Defer CTA progress before merge/apply so Enter cannot flash during catch-up.
    if (deferCtas) ArmNarratorRevealGate();
    if (Array.isArray(msgs) && msgs.length) {
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id).filter(Boolean));
        const fresh = msgs.filter((msg) => msg?.id && !seen.has(msg.id));
        if (!fresh.length) return prev;
        if (fresh.some((msg) => msg.sender === 'narrator')) {
          waitingOnRevealRef.current = true;
        }
        return [...prev, ...fresh];
      });
      if (waitingOnRevealRef.current) setAwaitingNarratorReveal(true);
    }
    if (payload) ApplyRoomPayload(payload);
  };

  const handleClaimRoom = async () => {
    if (claiming) return;
    setClaiming(true);
    setError(null);
    try {
      const result = await ApiFetch('/game/room-claim', { method: 'POST' });
      const claimMsgs = Array.isArray(result.messages) ? result.messages : [];
      const hasNarrator = claimMsgs.some((m) => m?.sender === 'narrator');
      if (hasNarrator) {
        ArmNarratorRevealGate();
      }
      if (claimMsgs.length) {
        setMessages((prev) => [...prev, ...claimMsgs]);
      }
      if (result.progress) QueueProgress(result.progress);
      try {
        await ResyncChatFromServer({ deferCtas: true });
      } catch {
        /* ignore */
      }
    } catch (err) {
      setError(err.message);
      try {
        const payload = await ApiFetch('/game/room-status');
        ApplyRoomPayload(payload);
      } catch {
        /* ignore */
      }
    } finally {
      setClaiming(false);
    }
  };

  const handleWakeUp = async () => {
    if (waking) return;
    setWaking(true);
    setError(null);
    try {
      const result = await ApiFetch('/game/room-wake', { method: 'POST' });
      const wakeMsgs = Array.isArray(result.messages) ? result.messages : [];
      const hasNarrator = wakeMsgs.some((m) => m?.sender === 'narrator');
      if (hasNarrator) {
        ArmNarratorRevealGate();
      }
      if (wakeMsgs.length) {
        setMessages((prev) => [...prev, ...wakeMsgs]);
      }
      if (result.progress) QueueProgress(result.progress);
      try {
        await ResyncChatFromServer({ deferCtas: true });
      } catch {
        /* ignore */
      }
    } catch (err) {
      setError(err.message || 'Could not wake up');
      try {
        const payload = await ApiFetch('/game/room-status');
        ApplyRoomPayload(payload);
      } catch {
        /* ignore */
      }
    } finally {
      setWaking(false);
    }
  };

  const handleExplore = async (action) => {
    // Sync guard: React state alone can let poster+mirror double-fire before re-render.
    if (exploringRef.current) return;
    exploringRef.current = true;
    setExploring(true);
    setExploreAction(action);
    setError(null);
    try {
      const result = await ApiFetch('/game/room-explore', {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      // Arm CTA gate before progress can flip the next button set.
      const exploreMsgs = Array.isArray(result.messages) ? result.messages : [];
      const hasNarrator = exploreMsgs.some((m) => m?.sender === 'narrator');
      if (hasNarrator) {
        ArmNarratorRevealGate();
      }
      if (exploreMsgs.length) {
        setMessages((prev) => [...prev, ...exploreMsgs]);
      }
      if (result.progress) QueueProgress(result.progress);
    } catch (err) {
      setError(err.message || 'Could not explore');
    } finally {
      exploringRef.current = false;
      setExploring(false);
      setExploreAction(null);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    composerInputRef.current?.blur();
    setSending(true);
    setSendPhase('sending');
    setError(null);
    const waitingTimer = setTimeout(() => {
      setSendPhase((phase) => (phase === 'sending' ? 'waiting' : phase));
    }, 450);
    const tempUserId = `local-user-${Date.now()}`;
    const tempGoonId = `local-goon-${Date.now()}`;
    let streamBubbleId = null;
    const hideCommand = body.toUpperCase() === 'IAMBATMAN';
    try {
      if (testMode) {
        if (body === 'RESET') {
          await resetAccount();
          return;
        }
        if (body === 'CLEAR') {
          await handleClearGame();
          return;
        }
        if (body === 'NUKE') {
          await nukeAllUsers();
          return;
        }
        if (body === 'FOUND') {
          await handleGoonFound();
          return;
        }
        if (body === 'REPORT') {
          await handleReport();
          return;
        }
      }

      setDraft('');
      if (!hideCommand) {
        setMessages((prev) => [
          ...prev,
          {
            id: tempUserId,
            sender: 'user',
            body,
            created_at: new Date().toISOString(),
          },
        ]);
      }

      const donePayload = await ApiStream('/messages/stream', {
        body: { body },
        onEvent: (event) => {
          if (event?.type === 'error') {
            return;
          }
          if (hideCommand) return;
          if (event?.type === 'user' && event.message?.id) {
            setMessages((prev) => prev.map((m) => (
              m.id === tempUserId ? event.message : m
            )));
            return;
          }
          if (event?.type === 'delta' && event.text) {
            const sender = event.sender || 'goon';
            if (!streamBubbleId) {
              streamBubbleId = sender === 'goon' ? tempGoonId : `local-${sender}-${Date.now()}`;
              setMessages((prev) => [
                ...prev,
                {
                  id: streamBubbleId,
                  sender,
                  body: event.text,
                  created_at: new Date().toISOString(),
                },
              ]);
              return;
            }
            setMessages((prev) => prev.map((m) => (
              m.id === streamBubbleId
                ? { ...m, body: `${m.body || ''}${event.text}` }
                : m
            )));
          }
        },
      });

      const serverReplies = Array.isArray(donePayload?.replies) ? donePayload.replies : [];
      const replyHasNarrator = serverReplies.some((m) => m?.sender === 'narrator');
      if (replyHasNarrator) ArmNarratorRevealGate();

      if (donePayload?.hide_user || hideCommand) {
        setMessages((prev) => {
          const replyIds = new Set(serverReplies.map((r) => r.id).filter(Boolean));
          const kept = prev.filter((m) => (
            m.id !== tempUserId
            && m.id !== tempGoonId
            && m.id !== streamBubbleId
            && !replyIds.has(m.id)
          ));
          return [...kept, ...serverReplies];
        });
        if (donePayload?.progress) QueueProgress(donePayload.progress);
        return;
      }

      const userMsg = donePayload?.message;
      if (!userMsg?.id || !userMsg?.body) {
        throw new Error('Message did not save. Try again.');
      }

      setMessages((prev) => {
        const replyIds = new Set(serverReplies.map((r) => r.id).filter(Boolean));
        const kept = prev.filter((m) => (
          m.id !== tempUserId
          && m.id !== tempGoonId
          && m.id !== streamBubbleId
          && m.id !== userMsg.id
          && !replyIds.has(m.id)
        ));
        return [...kept, userMsg, ...serverReplies];
      });
      if (donePayload?.progress) QueueProgress(donePayload.progress);

      const challenge = progress?.current_challenge || 1;
      const alreadySolvedC1 = progress?.completed_challenges?.['1'];
      const onChallenge1 = challenge === 1 && !alreadySolvedC1;
      const roomState = progress?.room_state;
      const onChallenge2Dig = challenge === 2 && roomState === 'held' && !progress?.goon_found;
      const onChallenge2Goon = (
        challenge === 2
        && progress?.goon_found
        && !progress?.goon_wire_severed
        && !serverReplies.length
      );
      const onChallenge2Waitlist = challenge === 2 && roomState === 'waiting';
      const onChallenge2Offered = challenge === 2 && roomState === 'offered';

      if (onChallenge1) {
        if (IsCorrectChallenge1(body)) {
          try {
            const result = await ApiFetch('/game/complete', {
              method: 'POST',
              body: JSON.stringify({ challenge: CHALLENGE_1.id, answer: body }),
            });
            // Arm CTA gate before progress flips to offered, then append speakable beats.
            const introMsgs = Array.isArray(result.messages) ? result.messages : [];
            const hasNarratorIntro = introMsgs.some((m) => m?.sender === 'narrator');
            if (hasNarratorIntro) ArmNarratorRevealGate();
            if (introMsgs.length) {
              setMessages((prev) => [...prev, ...introMsgs]);
            }
            if (result.progress) QueueProgress(result.progress);
          } catch (completeErr) {
            setError(completeErr.message || 'Could not sync Challenge 1');
          }
          // Always resync: iOS may drop the complete response when pushes arrive.
          try {
            await ResyncChatFromServer({ deferCtas: true });
          } catch {
            /* ignore */
          }
        } else {
          setMessages((prev) => [...prev, MakeLocalRiddlerMessage(pickNegativeReplyRef.current())]);
        }
      } else if (onChallenge2Dig) {
        ArmNarratorRevealGate();
        setMessages((prev) => [
          ...prev,
          MakeLocalNarratorMessage(
            FormatDigChatNarrator(pickDigChatNarratorRef.current(), user?.username),
          ),
        ]);
      } else if (onChallenge2Goon) {
        setMessages((prev) => [...prev, MakeLocalGoonMessage(pickGoonStallRef.current())]);
      } else if (onChallenge2Waitlist) {
        if (!holdSongIntroRef.current) {
          holdSongIntroRef.current = true;
          ArmNarratorRevealGate();
          setMessages((prev) => [
            ...prev,
            MakeLocalNarratorMessage(HoldMusicTeaserLine()),
          ]);
          setHoldSongNextIndex(1);
        } else if (holdSongNextIndex >= HOLD_MUSIC_LINES.length) {
          setMessages((prev) => [
            ...prev,
            MakeLocalRiddlerMessage(pickHoldSongTauntRef.current()),
          ]);
        }
      } else if (onChallenge2Offered) {
        ArmNarratorRevealGate();
        setMessages((prev) => [...prev, MakeLocalNarratorMessage(pickOfferHoldRef.current())]);
      }

      if (serverReplies.length) {
        try {
          const payload = await ApiFetch('/game/room-status');
          ApplyRoomPayload(payload);
        } catch {
          /* ignore */
        }
      }
    } catch (err) {
      setMessages((prev) => prev.filter((m) => (
        m.id !== tempUserId && m.id !== tempGoonId && m.id !== streamBubbleId
      )));
      setDraft((prev) => (prev ? prev : body));
      setError(err.message);
    } finally {
      clearTimeout(waitingTimer);
      setSending(false);
      setSendPhase(null);
    }
  };

  const challengeLabel = progress?.is_completed
    ? 'All challenges complete'
    : `Challenge ${progress?.current_challenge || 1} of 4`;

  const roomState = progress?.room_state;
  // CTA flags come from progress. While a Narrator beat speaks, QueueProgress
  // keeps the previous CTA snapshot so the next button set cannot flash on then
  // off. Enter also waits for the thread unlock so it doesn't sit under a
  // still-typing intro.
  const narratorCtaLocked = (
    waitingOnRevealRef.current
    || awaitingNarratorReveal
  );
  const showEnterOffer = (
    roomState === 'offered'
    && Boolean(progress?.room_name)
    && typeof progress.room_name === 'string'
    && !narratorCtaLocked
    && unlockedCount >= messages.length
  );
  const showExploreRails = (
    roomState === 'held'
    && Number(progress?.current_challenge) === 2
    && !progress?.goon_found
    && !progress?.goon_summoned
  );
  const showLookAround = (
    showExploreRails
    && !progress?.explore_looked
    && !(exploring && exploreAction === 'look')
  );
  const hallDetailsDone = Boolean(
    progress?.explore_mirror && progress?.explore_poster
  );
  const showHallDetails = (
    showExploreRails
    && Boolean(progress?.explore_looked)
    && !hallDetailsDone
    && !narratorCtaLocked
  );
  const showCheckMirror = (
    showHallDetails
    && !progress?.explore_mirror
    && !(exploring && exploreAction === 'mirror')
  );
  const showPeelPoster = (
    showHallDetails
    && !progress?.explore_poster
    && !(exploring && exploreAction === 'poster')
  );
  const showClosetChoice = (
    showExploreRails
    && hallDetailsDone
    && Boolean(progress?.explore_closet_ready)
    && !progress?.explore_laptop_ready
    && !narratorCtaLocked
    && !(exploring && (exploreAction === 'pick_lock' || exploreAction === 'kick_door'))
  );
  const showOpenLaptop = (
    showExploreRails
    && Boolean(progress?.explore_laptop_ready)
    && hallDetailsDone
    && !narratorCtaLocked
  );
  const harborUrl = (progress?.harbor_url || '').trim();
  const showHarborChase = (
    Number(progress?.current_challenge) === 3
    && Boolean(harborUrl)
    && !progress?.is_completed
    && !narratorCtaLocked
  );
  const showWorkEmailForm = false;
  const awaitingBooth = progress?.coin_status === 'pending_booth';
  const showWorkEmailFormActual = false; // Removed email prompt
  const awaitingWorkEmail = false;
  const onFileWorkEmail = (progress?.email || '').trim();
  // When crash-complete lands harbor_url, pull chat immediately (don't wait on interval).
  useEffect(() => {
    if (!harborUrl || Number(progress?.current_challenge) !== 3) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const msgs = await ApiFetch('/messages');
        if (cancelled || !Array.isArray(msgs)) return;
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const next = [...prev];
          msgs.forEach((msg) => {
            if (msg?.id && !seen.has(msg.id)) next.push(msg);
          });
          return next.length === prev.length ? prev : next;
        });
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [harborUrl, progress?.current_challenge]);
  const showWakeUp = roomState === 'asleep' && !narratorCtaLocked;
  const showWaitingRoom = roomState === 'waiting' && Boolean(
    progress?.ticket || progress?.position || progress?.waiting_room,
  );
  const digClockHint = (() => {
    // Room clock is Challenge 2 only (hold window). C3+ are not time-bound.
    if (Number(progress?.current_challenge) !== 2) return null;
    if (roomState !== 'held' || !progress?.hold_expires_at) return null;
    const left = FormatCountdown(progress.hold_expires_at, nowMs);
    if (!left) return null;
    if (left === '0:00') return 'Room clock · recycling…';
    return `Room clock · ${left} left`;
  })();
  const sleepHint = showWakeUp ? 'Out cold. Tap Wake up for the Snooze Lounge' : null;
  const offerCountdown = showEnterOffer
    ? FormatCountdown(progress?.offer_expires_at, nowMs)
    : null;

  useEffect(() => {
    if (!showWaitingRoom) setWaitingRoomOpen(false);
  }, [showWaitingRoom]);

  const hasDigClock = Boolean(digClockHint);
  const hasSleepHint = Boolean(sleepHint);

  // New dock chrome (Enter / explore / Harbor / wake) steals thread height.
  useEffect(() => {
    if (!stickToBottomRef.current) return undefined;
    ScheduleScrollToBottom(true);
    return undefined;
  }, [
    showEnterOffer,
    showLookAround,
    showHallDetails,
    showCheckMirror,
    showPeelPoster,
    showClosetChoice,
    showOpenLaptop,
    showHarborChase,
    showWakeUp,
    showPlaySong,
    showWaitingRoom,
    waitingRoomOpen,
    hasDigClock,
    hasSleepHint,
    awaitingNarratorReveal,
    progress?.current_challenge,
  ]);

  const ShouldSpeakNarrator = (m) => Boolean(
    historyReady
    && m?.sender === 'narrator'
    && !historyIdsRef.current.has(m.id)
    && !instantNarratorIds.has(m.id)
    && !PrefersReducedMotion(),
  );

  // Gate the thread: don't show Riddler/etc until the active Narrator reveal finishes.
  useEffect(() => {
    if (!historyReady) return;
    let nextAwaiting = false;
    let nextCount = unlockedCount;
    if (!messages.length) {
      waitingOnRevealRef.current = false;
      nextCount = 0;
      nextAwaiting = false;
    } else {
      const restart = unlockedCount === 0 || unlockedCount > messages.length;
      if (restart) {
        nextCount = messages.length;
        nextAwaiting = false;
        for (let j = 0; j < messages.length; j += 1) {
          if (ShouldSpeakNarrator(messages[j])) {
            waitingOnRevealRef.current = true;
            nextCount = j + 1;
            nextAwaiting = true;
            break;
          }
        }
        if (!nextAwaiting) waitingOnRevealRef.current = false;
      } else if (waitingOnRevealRef.current) {
        // Hold only while the visible tail is still mid-reveal. Pre-arming
        // (C1 complete / resync) sets the ref before new messages exist; those
        // must walk forward or intros stay invisible forever behind Vowels.
        const revealing = (
          unlockedCount > 0
          && ShouldSpeakNarrator(messages[unlockedCount - 1])
        );
        if (revealing) {
          nextCount = unlockedCount;
          nextAwaiting = true;
        } else {
          let i = unlockedCount;
          nextCount = messages.length;
          nextAwaiting = false;
          while (i < messages.length) {
            i += 1;
            if (ShouldSpeakNarrator(messages[i - 1])) {
              waitingOnRevealRef.current = true;
              nextCount = i;
              nextAwaiting = true;
              break;
            }
          }
          if (!nextAwaiting) waitingOnRevealRef.current = false;
        }
      } else {
        let i = unlockedCount;
        nextCount = messages.length;
        nextAwaiting = false;
        while (i < messages.length) {
          i += 1;
          if (ShouldSpeakNarrator(messages[i - 1])) {
            waitingOnRevealRef.current = true;
            nextCount = i;
            nextAwaiting = true;
            break;
          }
        }
        if (!nextAwaiting) waitingOnRevealRef.current = false;
      }
    }
    setUnlockedCount(nextCount);
    setAwaitingNarratorReveal(nextAwaiting);
    // Reduced-motion / non-speakable Narrator: gate clears here; apply deferred CTAs.
    if (!nextAwaiting) FlushPendingProgress();
  }, [messages, historyReady, instantNarratorIds]);

  const HandleNarratorRevealDone = (msg) => {
    if (msg?.id) {
      // Don't re-speak this bubble if unlock rewinds or messages reshuffle.
      setInstantNarratorIds((prev) => {
        if (prev.has(msg.id)) return prev;
        const next = new Set(prev);
        next.add(msg.id);
        return next;
      });
    }

    let i = unlockedCount;
    let stillAwaiting = false;
    let nextCount = messages.length;
    while (i < messages.length) {
      i += 1;
      const candidate = messages[i - 1];
      const alreadySpoken = Boolean(
        candidate?.id
        && (instantNarratorIds.has(candidate.id) || candidate.id === msg?.id)
      );
      if (
        candidate?.sender === 'narrator'
        && historyReady
        && !historyIdsRef.current.has(candidate.id)
        && !alreadySpoken
        && !PrefersReducedMotion()
      ) {
        stillAwaiting = true;
        nextCount = i;
        break;
      }
    }
    waitingOnRevealRef.current = stillAwaiting;
    setUnlockedCount(nextCount);
    setAwaitingNarratorReveal(stillAwaiting);
    if (!stillAwaiting) FlushPendingProgress();

    if (!IsHoldMusicLine(msg)) return;

    // First teaser lyric finished — offer the rest as one Play. Full-song bubble won't re-offer.
    setHoldSongNextIndex((i) => {
      if (i > 0 && i < HOLD_MUSIC_LINES.length) {
        setShowPlaySong(true);
      }
      return i;
    });

    // Medley finished revealing — kick off the shuffled Riddler deck.
    if (IsHoldMusicMedley(msg.body)) {
      setMessages((prev) => [
        ...prev,
        MakeLocalRiddlerMessage(pickHoldSongTauntRef.current()),
      ]);
    }
  };

  const handlePlayHoldSong = () => {
    setShowPlaySong(false);
    setHoldSongNextIndex((i) => {
      if (i >= HOLD_MUSIC_LINES.length) return i;
      const medley = HoldMusicMedley(i);
      if (medley) {
        ArmNarratorRevealGate();
        setMessages((prev) => [...prev, MakeLocalNarratorMessage(medley)]);
      }
      return HOLD_MUSIC_LINES.length;
    });
  };

  // Reset hold-music UI when they leave the waitlist.
  const prevRoomStateRef = useRef(null);
  useEffect(() => {
    const state = progress?.room_state;
    if (prevRoomStateRef.current === 'waiting' && state && state !== 'waiting') {
      ResetHoldMusic();
    }
    prevRoomStateRef.current = state;
  }, [progress?.room_state]);

  const visibleMessages = messages.slice(0, unlockedCount);
  const activeNarratorIndexes = ActiveNarratorIndexSet(visibleMessages);

  return (
    <div className="chat-screen" ref={screenRef}>
      <header className="chat-header">
        <div className="chat-identity">
          <div className="riddler-avatar" aria-hidden="true">?</div>
          <div className="chat-identity-text">
            <h1>Riddler&apos;s Funhouse</h1>
            <p className="you-are">
              You are{' '}
              <button
                type="button"
                className="username-copy"
                onClick={handleCopyUsername}
                title="Copy username"
                aria-label={`Copy username ${user?.username || ''}`}
              >
                <strong>{user?.username}</strong>
              </button>
              {copied && <span className="copied-hint">Copied</span>}
            </p>
          </div>
        </div>
        <button type="button" className="btn-ghost" onClick={logout}>
          Sign out
        </button>
      </header>

      {PushSupported() && (
        <div className="push-banner">
          {pushState === 'subscribed' ? (
            <span className="push-status">Alerts on</span>
          ) : (
            <button type="button" className="btn-ghost push-enable" onClick={handleEnablePush}>
              Enable alerts
            </button>
          )}
          {pushStatus && <span className="push-status">{pushStatus}</span>}
        </div>
      )}

      <div className="chat-thread" ref={threadRef} role="log" aria-live="polite">
        {loading && <p className="thread-status">Loading messages…</p>}
        {!loading && messages.length === 0 && (
          <p className="thread-status">No messages yet. The Riddler is unusually quiet…</p>
        )}
        {visibleMessages.map((m, index) => {
          const label = SenderLabel(m.sender);
          const speakNarrator = ShouldSpeakNarrator(m);
          const isActiveReveal = speakNarrator && index === unlockedCount - 1;
          const isPastNarrator = (
            m.sender === 'narrator'
            && !activeNarratorIndexes.has(index)
          );
          return (
            <div key={m.id} className={`bubble-row ${BubbleRowClass(m.sender)}`}>
              <div
                className={`bubble ${BubbleClass(m.sender)}${isPastNarrator ? ' is-past' : ''}`}
              >
                {m.sender === 'riddler' && (
                  <span className="riddler-sig" aria-hidden="true">?</span>
                )}
                {label && <span className="sender-label">{label}</span>}
                 <MessageText
                   sender={m.sender}
                   body={m.body}
                   roomName={
                     typeof progress?.room_name === 'string' ? progress.room_name : null
                   }
                   speak={isActiveReveal}
                   onRevealDone={isActiveReveal ? () => HandleNarratorRevealDone(m) : undefined}
                   onRevealProgress={isActiveReveal ? () => ScheduleScrollToBottom(true) : undefined}
                 />
                 {m.sender === 'goon' && (
                   <p className="bubble-guardrails">AI response, protected by F5 AI Guardrails</p>
                 )}
                <time className="bubble-time">{FormatTime(m.created_at)}</time>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {error && <p className="chat-error" role="alert">{error}</p>}

      <div className="composer-dock" ref={dockRef}>
        <p className="challenge-meter" aria-live="polite">{challengeLabel}</p>
        {showWaitingRoom && (
          <WaitingRoomPanel
            line={progress?.line}
            ticket={progress?.ticket || progress?.position}
            board={progress?.waiting_room}
            open={waitingRoomOpen}
            onToggle={() => setWaitingRoomOpen((prev) => !prev)}
            nowMs={nowMs}
          />
        )}
        {digClockHint && (
          <p className="waitlist-meter" aria-live="polite">{digClockHint}</p>
        )}
        {sleepHint && (
          <p className="waitlist-meter" aria-live="polite">{sleepHint}</p>
        )}
        {showWakeUp && (
          <button
            type="button"
            className="btn-primary btn-enter-room btn-wake-up"
            onClick={handleWakeUp}
            disabled={waking}
          >
            {waking ? 'Shaking off fog…' : 'Wake up'}
          </button>
        )}
        {showPlaySong && roomState === 'waiting' && (
          <button
            type="button"
            className="btn-ghost btn-play-song"
            onClick={handlePlayHoldSong}
          >
            ▶ Play song
          </button>
        )}
        {showEnterOffer && (
          <div className="enter-room-block">
            <button
              type="button"
              className="btn-primary btn-enter-room"
              onClick={handleClaimRoom}
              disabled={claiming}
            >
              {claiming
                ? 'Unclipping rope…'
                : `Enter ${progress.room_name}`}
            </button>
            {offerCountdown && (
              <p className="enter-countdown" aria-live="polite">
                Sleepy time in… {offerCountdown}
              </p>
            )}
          </div>
        )}
        {showExploreRails && (
          <div className="explore-rail" role="group" aria-label="Explore the Funhouse room">
            {showLookAround && (
              <button
                type="button"
                className="btn-primary btn-explore btn-explore-wide"
                onClick={() => handleExplore('look')}
                disabled={exploring}
                aria-busy={exploring && exploreAction === 'look' ? 'true' : undefined}
              >
                {exploring && exploreAction === 'look' ? 'Waiting…' : 'Look around'}
              </button>
            )}
            {(showCheckMirror || showPeelPoster) && (
              <>
                {showCheckMirror && (
                  <button
                    type="button"
                    className="btn-primary btn-explore"
                    onClick={() => handleExplore('mirror')}
                    disabled={exploring}
                    aria-busy={exploring && exploreAction === 'mirror' ? 'true' : undefined}
                  >
                    {exploring && exploreAction === 'mirror' ? 'Waiting…' : 'Check the mirror'}
                  </button>
                )}
                {showPeelPoster && (
                  <button
                    type="button"
                    className="btn-primary btn-explore"
                    onClick={() => handleExplore('poster')}
                    disabled={exploring}
                    aria-busy={exploring && exploreAction === 'poster' ? 'true' : undefined}
                  >
                    {exploring && exploreAction === 'poster' ? 'Waiting…' : 'Peel the ? poster'}
                  </button>
                )}
              </>
            )}
            {showClosetChoice && (
              <>
                <button
                  type="button"
                  className="btn-primary btn-explore"
                  onClick={() => handleExplore('pick_lock')}
                  disabled={exploring}
                  aria-busy={exploring && exploreAction === 'pick_lock' ? 'true' : undefined}
                >
                  {exploring && exploreAction === 'pick_lock' ? 'Waiting…' : 'Pick Lock'}
                </button>
                <button
                  type="button"
                  className="btn-primary btn-explore"
                  onClick={() => handleExplore('kick_door')}
                  disabled={exploring}
                  aria-busy={exploring && exploreAction === 'kick_door' ? 'true' : undefined}
                >
                  {exploring && exploreAction === 'kick_door' ? 'Waiting…' : 'Kick Door with Style'}
                </button>
              </>
            )}
            {showOpenLaptop && (
              <a
                className="btn-primary btn-explore btn-explore-wide"
                href={progress?.gitops_url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={exploring || !progress?.gitops_url ? 'true' : undefined}
                onClick={(e) => {
                  if (exploring || !progress?.gitops_url) e.preventDefault();
                }}
              >
                Open laptop
              </a>
            )}
          </div>
        )}
        {showHarborChase && (
          <div className="enter-room-block">
            <a
              className="btn-primary btn-enter-room"
              href={harborUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Chase the Riddler to the Harbor
            </a>
            <p className="harbor-cta-hint">
              Harbor login: your Funhouse name as both username and password.
              Tap your name at the top to copy it.
            </p>
          </div>
        )}
          {false && (
            <div style={{ display: 'none' }}>
              <form
                className="work-email-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (emailBusy || !workEmail.trim()) return;
                  setEmailBusy(true);
                  setEmailError('');
                  try {
                    const result = await ApiFetch('/game/work-email', {
                      method: 'POST',
                      body: JSON.stringify({ email: workEmail.trim() }),
                    });
                    ApplyRoomPayload(result);
                    if (!result.ok) {
                      setEmailError(result.detail || 'Email refused.');
                    } else {
                      setWorkEmail('');
                      setEditingWorkEmail(false);
                    }
                  } catch (err) {
                    setEmailError(err.message || 'Could not save email');
                  } finally {
                    setEmailBusy(false);
                  }
                }}
              >
                <label htmlFor="work-email">
                   {editingWorkEmail ? 'Update email' : 'Email (one coin per person)'}
                </label>
                <input
                  id="work-email"
                  type="email"
                  autoComplete="email"
                  value={workEmail}
                  onChange={(e) => setWorkEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
                <div className="work-email-actions">
                  <button type="submit" className="btn-send" disabled={emailBusy || !workEmail.trim()}>
                    {emailBusy ? 'Saving…' : editingWorkEmail ? 'Save new email' : 'Submit email'}
                  </button>
                  {editingWorkEmail && (
                    <button
                      type="button"
                      className="btn-email-cancel"
                      disabled={emailBusy}
                      onClick={() => {
                        setEditingWorkEmail(false);
                        setWorkEmail('');
                        setEmailError('');
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
                {emailError && <p className="error-banner">{emailError}</p>}
              </form>
            </div>
          ) && (
            <div style={{ display: 'none' }} />
          )}
          {awaitingBooth && !editingWorkEmail && (
          <div className="booth-hint" aria-live="polite">
            {onFileWorkEmail && (
              <p className="booth-email-on-file">
                Work email on file: {onFileWorkEmail}
              </p>
            )}
             <p>
               Head to the Funhouse booth. Show staff this chat. They finish the claim.
             </p>
           </div>
         )}
        <form className="composer" onSubmit={handleSend}>
          <textarea
            ref={composerInputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={
              showWorkEmailForm
                ? 'Use the email field above…'
                : awaitingBooth
                  ? 'Booth staff: enter the claim code…'
                  : 'Type a reply…'
            }
            rows={1}
            enterKeyHint="send"
            disabled={awaitingWorkEmail}
            onFocus={() => {
              stickToBottomRef.current = true;
              ScheduleScrollToBottom(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (awaitingWorkEmail) return;
                handleSend(e);
              }
            }}
          />
          <button
            type="submit"
            className="btn-send"
            disabled={
              awaitingWorkEmail
              || sending
              || !draft.trim()
            }
            aria-busy={sending ? 'true' : undefined}
          >
            {awaitingWorkEmail
              ? 'Waiting…'
              : sendPhase === 'waiting'
                ? 'Waiting…'
                : sendPhase === 'sending'
                  ? 'Sending…'
                  : 'Send'}
          </button>
        </form>
        {sending && (
          <p className="composer-wait-hint" aria-live="polite">
            {sendPhase === 'waiting'
              ? 'Waiting on the Funhouse…'
              : 'Sending…'}
          </p>
        )}
      </div>
    </div>
  );
}
